import {
    LIMITE_MAXIMO,
    LIMITE_MINIMO,
    MAX_COLUNAS_AGRUPAMENTO,
    MAX_METRICAS,
    nomeMetrica,
    podeAgrupar,
    podeSomar,
} from './agrupamentoUi'

const DATA_RE = /^\d{4}-\d{2}-\d{2}$/
const DIRECOES = ['asc', 'desc']
// Mesma lista do validarDefinicao.js do server.
const OPERADORES = ['igual', 'diferente', 'contem', 'maior', 'menor']
const FUNCOES_METRICA = ['soma', 'media', 'contagem']

// Traduz a definição que a IA devolveu para o estado do montador. Se citar
// dataset ou coluna que não existe no catálogo, devolve null — e a tela mantém
// o que o usuário já tinha escolhido, em vez de virar um formulário quebrado.
//
// Tudo que muda QUAIS linhas voltam (dataset, colunas, agrupamento, contas,
// ordenação, filtros, limite) é tudo-ou-nada: descartar um filtro em silêncio
// traria mais dados do que o usuário pediu, sem ele perceber. Só o período
// degrada sozinho, porque os campos De/Até ficam à vista mostrando o que vale.
export function aplicarDefinicaoNoBuilder(definicao, datasets) {
    if (!definicao || typeof definicao !== 'object') {
        return null
    }

    const lista = Array.isArray(datasets) ? datasets : []
    const dataset = lista.find(item => item && item.id === definicao.dataset)
    if (!dataset) {
        return null
    }

    const disponiveis = (dataset.colunas || []).map(coluna => coluna.nome)

    const agrupamento = lerAgrupamento(definicao.agrupamento, dataset, disponiveis)
    if (agrupamento === false) {
        return null
    }

    // Agrupado, a linha é "agrupar por + contas": as colunas saem do agrupamento.
    const colunas = agrupamento ? [...agrupamento.por] : lerColunas(definicao.colunas, disponiveis)
    if (!colunas) {
        return null
    }

    // Agrupado, a ordenação pode citar uma conta (soma_valorTotal), que não está no catálogo.
    const ordenaveis = agrupamento
        ? [...agrupamento.por, ...agrupamento.metricas.map(nomeMetrica)]
        : disponiveis

    const ordenacao = lerOrdenacao(definicao.ordenacao, ordenaveis)
    if (ordenacao === false) {
        return null
    }

    const filtros = lerFiltros(definicao.filtros, disponiveis)
    if (filtros === false) {
        return null
    }

    const limite = lerLimite(definicao.limite)
    if (limite === false) {
        return null
    }

    const periodo = definicao.periodo
    const periodoValido = !!periodo
        && DATA_RE.test(periodo.inicio || '')
        && DATA_RE.test(periodo.fim || '')

    return {
        dataset: dataset.id,
        colunas,
        periodo: periodoValido ? { inicio: periodo.inicio, fim: periodo.fim } : null,
        ordenacao,
        filtros,
        // Ausentes ficam de fora do estado: o montador volta ao modo linha a linha.
        agrupamento: agrupamento || undefined,
        limite: limite === null ? undefined : limite,
    }
}

// Devolve as colunas pedidas, sem repetição, ou null quando alguma não existe.
function lerColunas(colunas, disponiveis) {
    if (!Array.isArray(colunas) || !colunas.length) {
        return null
    }

    const validas = []
    for (const nome of colunas) {
        if (!disponiveis.includes(nome)) {
            return null
        }
        if (!validas.includes(nome)) {
            validas.push(nome)
        }
    }

    return validas
}

// Devolve o agrupamento válido, null quando não veio, ou false quando veio quebrado.
function lerAgrupamento(agrupamento, dataset, disponiveis) {
    if (agrupamento === undefined || agrupamento === null) {
        return null
    }

    if (typeof agrupamento !== 'object' || Array.isArray(agrupamento)) {
        return false
    }

    // Mesas e comandas já vem somado do banco: agrupar de novo daria total errado.
    if (!podeAgrupar(dataset)) {
        return false
    }

    const por = agrupamento.por
    if (!Array.isArray(por) || !por.length || por.length > MAX_COLUNAS_AGRUPAMENTO) {
        return false
    }

    const colunasPor = []
    for (const nome of por) {
        // Conta (valor, quantidade) não separa: vai em métricas, nunca em `por`.
        if (!disponiveis.includes(nome) || colunasPor.includes(nome) || podeSomar(dataset, nome)) {
            return false
        }
        colunasPor.push(nome)
    }

    const metricas = agrupamento.metricas
    if (!Array.isArray(metricas) || !metricas.length || metricas.length > MAX_METRICAS) {
        return false
    }

    const validas = []
    const nomes = []
    for (const metrica of metricas) {
        if (!metrica || typeof metrica !== 'object') {
            return false
        }

        const funcao = String(metrica.funcao || '').toLowerCase()
        if (!FUNCOES_METRICA.includes(funcao)) {
            return false
        }

        // Soma e média só existem em coluna de número ou dinheiro.
        if (funcao !== 'contagem' && !podeSomar(dataset, metrica.coluna)) {
            return false
        }

        const nova = funcao === 'contagem' ? { funcao } : { funcao, coluna: metrica.coluna }
        const nome = nomeMetrica(nova)
        if (nomes.includes(nome)) {
            return false
        }

        nomes.push(nome)
        validas.push(nova)
    }

    return { por: colunasPor, metricas: validas }
}

// Devolve o limite válido, null quando não veio, ou false quando veio quebrado.
function lerLimite(limite) {
    if (limite === undefined || limite === null || limite === '') {
        return null
    }

    if (typeof limite !== 'number' && typeof limite !== 'string') {
        return false
    }

    const numero = Number(limite)
    if (!Number.isInteger(numero) || numero < LIMITE_MINIMO || numero > LIMITE_MAXIMO) {
        return false
    }

    return numero
}

// Devolve a ordenação válida, null quando não veio, ou false quando veio quebrada.
function lerOrdenacao(ordenacao, ordenaveis) {
    if (ordenacao === undefined || ordenacao === null) {
        return null
    }

    if (typeof ordenacao !== 'object' || Array.isArray(ordenacao)) {
        return false
    }

    if (!ordenaveis.includes(ordenacao.coluna)) {
        return false
    }

    const direcao = String(ordenacao.direcao || 'asc').toLowerCase()
    if (!DIRECOES.includes(direcao)) {
        return false
    }

    return { coluna: ordenacao.coluna, direcao }
}

// Devolve a lista de filtros válidos, [] quando não veio, ou false se algum quebrou.
function lerFiltros(filtros, disponiveis) {
    if (filtros === undefined || filtros === null) {
        return []
    }

    if (!Array.isArray(filtros)) {
        return false
    }

    const validos = []
    for (const filtro of filtros) {
        if (!filtro || typeof filtro !== 'object') {
            return false
        }
        if (!disponiveis.includes(filtro.coluna)) {
            return false
        }

        const operador = String(filtro.operador || '').toLowerCase()
        if (!OPERADORES.includes(operador)) {
            return false
        }

        const tipoValor = typeof filtro.valor
        if (tipoValor !== 'string' && tipoValor !== 'number' && tipoValor !== 'boolean') {
            return false
        }

        validos.push({ coluna: filtro.coluna, operador, valor: filtro.valor })
    }

    return validos
}
